import { Button, Card, CardActions, CardContent, SystemIcon, Typography } from '@volvo/vcdk-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { ITelemetryService } from '@/service/vpc/types/ITelemetryService';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import DigitalServiceModal, {
  type DigitalServiceModalHandle,
} from '@/components/modals/digital-service/DigitalServiceModal';

export type ServiceCardStatus = 'active' | 'activatable' | 'request-sent' | 'requires-prerequisite';

type ServiceCardProps = {
  segment: 'Marine' | 'Industrial';
  endCustomerUnitId: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  checklist?: string[];
  readMoreHref?: string;
  onReadMore?: () => void;
  services: ITelemetryService[];
  deactivationServices: ITelemetryService[];
  status: ServiceCardStatus;
  requiresLabel?: string;
};

const ServiceCard: React.FC<ServiceCardProps> = ({
  segment,
  endCustomerUnitId,
  title,
  description,
  checklist,
  readMoreHref,
  onReadMore,
  services,
  deactivationServices,
  status,
  requiresLabel,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<DigitalServiceModalHandle>(null);

  const isActive = status === 'active';

  const renderPrimaryButton = () => {
    switch (status) {
      case 'active':
        return (
          <Button variant="secondary" onClick={() => modalRef.current?.openTerminate()}>
            {t(($) => $.pages.unit.services.terminate_button)}
          </Button>
        );
      case 'activatable':
        // `marketing` renders the design's blue call-to-action treatment.
        return (
          <Button variant="marketing" onClick={() => modalRef.current?.openActivate()}>
            {t(($) => $.pages.unit.services.activate_button)}
          </Button>
        );
      case 'request-sent':
        // Disabled `primary` renders the design's solid bg-disabled (#E1E1E1) fill; `secondary` keeps an outline.
        return (
          <Button variant="primary" disabled>
            {t(($) => $.pages.unit.services.request_sent)}
          </Button>
        );
      case 'requires-prerequisite':
        return (
          <Button variant="primary" disabled>
            {requiresLabel}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-6">
          <div
            className={`flex w-fit items-center justify-center rounded p-4 ${
              isActive ? 'bg-success-subtle' : 'bg-variant'
            }`}
          >
            <SystemIcon
              icon="verified"
              size={32}
              filled={isActive}
              className={
                isActive
                  ? '[&::part(svg)]:text-(--vcdk-color-icon-success)'
                  : '[&::part(svg)]:text-(--vcdk-color-icon-subtle)'
              }
            />
          </div>
          <div className="flex flex-col gap-2">
            <Typography variant="heading4">{title}</Typography>
            {!!description && (
              <Typography variant="body" className="text-secondary whitespace-pre-line">
                {description}
              </Typography>
            )}
          </div>
          {!!checklist?.length && (
            <ul className="flex flex-col gap-1">
              {checklist.map((item, index) => (
                <li key={index} className="flex items-start gap-4 py-3">
                  <SystemIcon
                    icon="checkmark-circle"
                    size={20}
                    className="shrink-0 [&::part(svg)]:text-(--vcdk-color-icon-subtle)"
                  />
                  <Typography variant="caption1" className="text-secondary">
                    {item}
                  </Typography>
                </li>
              ))}
            </ul>
          )}
        </div>
        <CardActions justifyContent="flex-start">
          {renderPrimaryButton()}
          {onReadMore ? (
            <Button variant="tertiary" onClick={onReadMore}>
              {t(($) => $.pages.unit.services.read_more_button)}
            </Button>
          ) : (
            !!readMoreHref && (
              <Button variant="tertiary" href={readMoreHref} target="_blank" rel="noopener noreferrer">
                {t(($) => $.pages.unit.services.read_more_button)}
              </Button>
            )
          )}
        </CardActions>
      </CardContent>
      <DigitalServiceModal
        ref={modalRef}
        segment={segment}
        endCustomerUnitId={endCustomerUnitId}
        services={services}
        deactivationServices={deactivationServices}
      />
    </Card>
  );
};

export default withComponentErrorBoundary(ServiceCard);
